import * as React from 'react';
import { useState } from 'react';
import { makeStyles, tokens, Button, Tooltip } from '@fluentui/react-components';
import { 
  DocumentPdfRegular, 
  DocumentRegular, 
  DocumentTextRegular,
  DismissRegular,
  AddRegular
} from '@fluentui/react-icons';

// Import centralized interfaces from parent interfaces folder
import type { ChatAttachmentsProps } from '../interfaces';

const useStyles = makeStyles({
  attachmentsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    maxWidth: '200px',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: '12px',
    minHeight: '24px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  fileName: {
    maxWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '11px',
  },
  removeButton: {
    minWidth: '16px',
    height: '16px',
    padding: '0',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  plusButton: {
    minWidth: '24px',
    height: '24px',
    padding: '0',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '250px',
  },
  tooltipFileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tooltipFileName: {
    flex: 1,
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tooltipRemoveButton: {
    minWidth: '18px',
    height: '18px',
    padding: '0',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusSmall,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

// Helper function to get file type icon
const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return <DocumentPdfRegular style={{ fontSize: '14px', color: tokens.colorPaletteRedBackground3 }} />;
    case 'doc':
    case 'docx':
      return <DocumentRegular style={{ fontSize: '14px', color: tokens.colorPaletteBlueBorderActive }} />;
    case 'txt':
    case 'md':
      return <DocumentTextRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground2 }} />;
    default:
      return <DocumentRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground2 }} />;
  }
};

const ChatAttachments: React.FC<ChatAttachmentsProps> = ({ 
  uploadedFiles = [], 
  onFileRemove,
  maxVisibleAttachments = 1
}) => {
//     fileCount: uploadedFiles.length, 
//     files: uploadedFiles.map(f => f.name)
//   });

  const styles = useStyles();
  const [showTooltip, setShowTooltip] = useState(false);

  if (uploadedFiles.length === 0) {
    return null;
  }

  const firstFile = uploadedFiles[0];
  const remainingCount = uploadedFiles.length - 1;

  const handleFileRemove = (fileName: string) => {
    if (onFileRemove) {
      onFileRemove(fileName);
    }
    // Close tooltip if we removed a file
    setShowTooltip(false);
  };

  const renderTooltipContent = () => (
    <div className={styles.tooltipContent}>
      <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
        All Files ({uploadedFiles.length})
      </div>
      {uploadedFiles.map((file, index) => (
        <div key={`tooltip-${file.name}-${index}`} className={styles.tooltipFileItem}>
          {getFileIcon(file.name)}
          <span className={styles.tooltipFileName} title={file.name}>
            {file.name}
          </span>
          <button
            className={styles.tooltipRemoveButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileRemove(file.name);
            }}
            title={`Remove ${file.name}`}
            type="button"
          >
            <DismissRegular style={{ fontSize: '12px' }} />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.attachmentsContainer}>
      {/* Show first file */}
      <div className={styles.fileItem}>
        {getFileIcon(firstFile.name)}
        <span className={styles.fileName} title={firstFile.name}>
          {firstFile.name}
        </span>
        <button
          className={styles.removeButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFileRemove(firstFile.name);
          }}
          title={`Remove ${firstFile.name}`}
          type="button"
        >
          <DismissRegular style={{ fontSize: '10px' }} />
        </button>
      </div>

      {/* Show +button if more than 1 file */}
      {remainingCount > 0 && (
        <Tooltip
          content={renderTooltipContent()}
          relationship="description"
          visible={showTooltip}
          onVisibleChange={(e, data) => setShowTooltip(data.visible)}
        >
          <button
            className={styles.plusButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
            title={`Show all ${uploadedFiles.length} files`}
            type="button"
          >
            +{remainingCount}
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default ChatAttachments;